import { FC, useEffect, useMemo } from 'react';
import { P2GBox, P2GDivider, P2GGrid, P2GLink, P2GText, isEmail } from '@parcel2go/core-ui';
import useBasketSection from '~/hooks/use-basket-section';
import Section from '~/components/core/section/section';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import OptIn from '~/components/parcel-details/account/opt-in';
import { useAuth } from 'react-oidc-context';
import { BasketSections } from '~/lib/types/enums';
import { BaseModuleProps } from './module-factory';
import AccountDetails from '~/components/parcel-details/account/account';
import { Account, QuoteAddress } from '~/lib/types/basket-types';
import { StandardFormWidths } from '~/lib/forms';
import SenderTypeCore from '~/components/sender-type/sender-type';
import SignIn from '~/components/parcel-details/account/sign-in';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';

const AccountModule: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { settings } = useTenant();
  const { isAuthenticated, user, signoutRedirect } = useAuth();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const account = useBasketSection<Account>({ name: BasketSections.ACCOUNT });
  const origin = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const onSignOut = () =>
    signoutRedirect({ post_logout_redirect_uri: settings[ConfigSetting.POSTLOGOUTREDIRECTURI] || '/' });

  useEffect(() => {
    if (
      isAuthenticated &&
      isEmail(user?.profile.preferred_username || '') &&
      account.data.email !== user?.profile.preferred_username &&
      !account.loading
    ) {
      account.update({
        ...account.data,
        email: user?.profile.preferred_username,
        forename: user?.profile.given_name,
        surname: user?.profile.family_name
      });
    }
  }, [account, user, isAuthenticated]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      origin.data.metadata?.basedOnAccount &&
      account.data.email &&
      account.data.forename &&
      account.data.surname &&
      (origin.data.email !== account.data.email ||
        origin.data.contactname !== `${account.data.forename} ${account.data.surname}` ||
        (account.data.mobile && origin.data.phone !== account.data.mobile))
    ) {
      origin.update({
        ...origin.data,
        email: account.data.email,
        phone: account.data.mobile,
        contactname: `${account.data.forename} ${account.data.surname}`
      });
    }
  }, [account, origin, isAuthenticated]);

  const hasAccount = useMemo(() => {
    return isAuthenticated && account.data.forename && account.data.surname && account.data.email;
  }, [account.data.email, account.data.forename, account.data.surname, isAuthenticated]);

  return wrapper(
    <Section
      title={
        hasAccount
          ? t('YOUR_DETAILS_TITLE_LOGGED_IN', {
              defaultValue: `Welcome, {{forename}}!`,
              forename: account.data.forename
            })
          : t('YOUR_DETAILS_TITLE', {
              defaultValue: 'Your Details'
            })
      }
      dataTestId="account-section"
      loading={account.loading}
    >
      <P2GGrid container spacing={2}>
        {hasAccount ? (
          <P2GGrid item xs={12}>
            <P2GText variant="body2" fontWeight="bold">
              <Trans
                i18nKey="YOUR_DETAILS_MESSAGE_LOGGED_IN"
                t={t}
                defaults="Logged in as {{forename}} {{surname}} ({{email}}). <0>Not you?</0>"
                values={{
                  forename: account.data.forename,
                  surname: account.data.surname,
                  email: account.data.email
                }}
                components={[<P2GLink onClick={onSignOut} />]}
              />
            </P2GText>
          </P2GGrid>
        ) : (
          <P2GGrid item {...StandardFormWidths}>
            <AccountDetails section={account} errors={errors} />
          </P2GGrid>
        )}
        {!isAuthenticated && settings[PublicSettings.ORDERPROCESS.SIGNINDISABLED] !== 'true' && (
          <P2GGrid item xs={12}>
            <SignIn />
          </P2GGrid>
        )}
        {account.data.email && (
          <>
            <OptIn
              wrapper={(x) => (
                <P2GGrid item xs={12}>
                  {x}
                </P2GGrid>
              )}
            />
            {settings[PublicSettings.ORDERPROCESS.SENDERTYPEENABLED] === 'true' && (
              <SenderTypeCore
                errors={errors}
                wrapper={(x) => (
                  <>
                    <P2GGrid item xs={12}>
                      <P2GDivider style={{ margin: 0 }} />
                    </P2GGrid>
                    <P2GGrid item xs={12}>
                      <P2GText variant="subtitle1" fontWeight="bold">
                        {t('SENDER_TYPE_TITLE', {
                          defaultValue: 'Why are you shipping today?'
                        })}
                      </P2GText>
                    </P2GGrid>
                    <P2GGrid item xs={12}>
                      <P2GBox dataTestId="sender-type-section">{x}</P2GBox>
                    </P2GGrid>
                  </>
                )}
              />
            )}
          </>
        )}
      </P2GGrid>
    </Section>
  );
};
export default AccountModule;
